import { useEffect, useMemo, useState } from 'react'
import api from '../services/api'

// Eventos da Reforma Tributária (NT 2025.002) nas NF-e de compra, aberto pelo Manifestador.
type Tipo = '211110' | '211124' | '211128' | '211130' | '211150'
interface Item { nItem: number; cProd: string; xProd: string; uCom: string; qCom: number; vProd: number; cClassTrib: string; vBC: number; vIBS: number; vCBS: number }
interface Nota { chave: string; numero: string; emissao: string; emitente: string; cnpjEmitente: string; finNFe: string; itens: Item[] }
interface Evento {
  seq: number; dataHora: string; tpEvento: string; evento: string; situacao: string; cancelado: boolean
  nSeqEvento: number | null; protocolo: string; cStat: number; motivo: string; usuario: string
}
interface Linha {
  marcado: boolean; vIBS: string; vCBS: string; quantidade: string; unidade: string
  cCredPres: string; pIBS: string; vCredIBS: string; pCBS: string; vCredCBS: string
}

const AJUDA: Record<Tipo, string> = {
  '211110': 'Pede o crédito presumido de IBS/CBS dos itens que dão direito, quando o fornecedor não informou na nota.',
  '211124': 'Informa itens perdidos, roubados, furtados ou perecidos no transporte contratado por vocês (frete FOB). Tira o crédito desses itens.',
  '211128': 'Responde se vocês concordam com o débito gerado pela Nota de Crédito do fornecedor.',
  '211130': 'Informa que o item foi para o ativo imobilizado (ônibus, motor, equipamento...).',
  '211150': 'Pede o crédito de IBS/CBS de bens e serviços cujo direito depende da atividade da empresa.',
}
const fmt = (v: number, c = 2) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const numBr = (s: string) => { const t = String(s ?? '').trim(); return t === '' ? NaN : Number(t.replace(/\./g, '').replace(',', '.')) }
const dataHora = (s: string) => { const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleString('pt-BR') }
const vazia = (it: Item): Linha => ({ marcado: false, vIBS: '', vCBS: '', quantidade: String(it.qCom).replace('.', ','), unidade: it.uCom, cCredPres: '', pIBS: '', vCredIBS: '', pCBS: '', vCredCBS: '' })

export default function EventosRtcModal({ chave, empCod, onClose }: { chave: string; empCod: number; onClose: () => void }) {
  const [nota, setNota] = useState<Nota | null>(null)
  const [eventos, setEventos] = useState<Evento[]>([])
  const [tipos, setTipos] = useState<{ codigo: Tipo; nome: string }[]>([])
  const [ambiente, setAmbiente] = useState<'hom' | 'prod'>('prod')
  const [tipo, setTipo] = useState<Tipo>('211130')
  const [linhas, setLinhas] = useState<Record<number, Linha>>({})
  const [aceite, setAceite] = useState<'1' | '0'>('1')
  const [carregando, setCarregando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null)

  async function carregar() {
    setCarregando(true); setErro('')
    try {
      const r = await api.get(`/manifestador-rtc/${chave}`)
      setNota(r.data.nota); setEventos(r.data.eventos); setTipos(r.data.tipos); setAmbiente(r.data.ambiente)
      setLinhas(Object.fromEntries(r.data.nota.itens.map((i: Item) => [i.nItem, vazia(i)])))
      if (r.data.nota.finNFe === '5') setTipo('211128')
    } catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao carregar a nota.') }
    finally { setCarregando(false) }
  }
  useEffect(() => { carregar() }, [chave])

  const set = (n: number, campo: keyof Linha, valor: any) => setLinhas(l => ({ ...l, [n]: { ...l[n], [campo]: valor } }))
  const marcados = useMemo(() => (nota?.itens ?? []).filter(i => linhas[i.nItem]?.marcado), [nota, linhas])
  const copiarDaNota = () => setLinhas(l => {
    const novo = { ...l }
    for (const i of marcados) novo[i.nItem] = { ...novo[i.nItem], vIBS: fmt(i.vIBS), vCBS: fmt(i.vCBS), vCredIBS: fmt(i.vIBS), vCredCBS: fmt(i.vCBS) }
    return novo
  })

  function montarCorpo() {
    if (tipo === '211128') return { tipo, indAceitacao: aceite }
    const itens = marcados.map(i => {
      const l = linhas[i.nItem]
      if (tipo === '211110') {
        const ibs = l.pIBS || l.vCredIBS ? { pCredPres: numBr(l.pIBS), vCredPres: numBr(l.vCredIBS) } : undefined
        const cbs = l.pCBS || l.vCredCBS ? { pCredPres: numBr(l.pCBS), vCredPres: numBr(l.vCredCBS) } : undefined
        return { nItem: i.nItem, vBC: i.vBC, cCredPres: l.cCredPres, ibs, cbs }
      }
      if (tipo === '211150') return { nItem: i.nItem, vCredIBS: numBr(l.vCredIBS), vCredCBS: numBr(l.vCredCBS) }
      return { nItem: i.nItem, vIBS: numBr(l.vIBS), vCBS: numBr(l.vCBS), quantidade: numBr(l.quantidade), unidade: l.unidade }
    })
    return { tipo, itens }
  }

  async function enviar() {
    if (!nota) return
    setMsg(null)
    if (tipo !== '211128' && !marcados.length) { setMsg({ ok: false, texto: 'Marque pelo menos um item.' }); return }
    const nome = tipos.find(t => t.codigo === tipo)?.nome ?? tipo
    const aviso = ambiente === 'prod'
      ? `ATENÇÃO: PRODUÇÃO — o evento vale de verdade na apuração do IBS/CBS.\n\nEnviar "${nome}" para a NF-e nº ${nota.numero}?`
      : `Ambiente de HOMOLOGAÇÃO (teste).\n\nEnviar "${nome}" para a NF-e nº ${nota.numero}?`
    if (!window.confirm(aviso)) return
    setEnviando(true)
    try {
      const r = await api.post(`/manifestador-rtc/${chave}/evento`, { ...montarCorpo(), empCod })
      const d = r.data
      setMsg({ ok: d.autorizado, texto: d.autorizado ? `Evento autorizado. Protocolo ${d.protocolo}.` : d.erro ? `Falha no envio: ${d.erro}` : `SEFAZ recusou (${d.cStat}): ${d.xMotivo}` })
      if (d.autorizado) await carregar()
    } catch (e: any) { setMsg({ ok: false, texto: e?.response?.data?.error || 'Erro ao enviar o evento.' }) }
    finally { setEnviando(false) }
  }

  async function cancelar(ev: Evento) {
    if (!window.confirm(`Cancelar o evento "${ev.evento}" (protocolo ${ev.protocolo})?${ambiente === 'prod' ? '\n\nATENÇÃO: PRODUÇÃO.' : ''}`)) return
    setEnviando(true); setMsg(null)
    try {
      const r = await api.post(`/manifestador-rtc/${chave}/cancelar`, { seq: ev.seq, empCod })
      const d = r.data
      setMsg({ ok: d.autorizado, texto: d.autorizado ? `Evento cancelado. Protocolo ${d.protocolo}.` : d.erro ? `Falha no envio: ${d.erro}` : `SEFAZ recusou (${d.cStat}): ${d.xMotivo}` })
      if (d.autorizado) await carregar()
    } catch (e: any) { setMsg({ ok: false, texto: e?.response?.data?.error || 'Erro ao cancelar o evento.' }) }
    finally { setEnviando(false) }
  }

  const th: React.CSSProperties = { padding: '6px', fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: '#1a6ea8', textAlign: 'left', whiteSpace: 'nowrap' }
  const td: React.CSSProperties = { padding: '4px 6px', fontSize: '0.78rem', borderBottom: '1px solid #eef2f7', verticalAlign: 'middle' }
  const tdN: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
  const inp = (n: number, campo: keyof Linha, w = 72, ph = '') => (
    <input value={linhas[n]?.[campo] as string ?? ''} placeholder={ph} disabled={!linhas[n]?.marcado}
      onChange={e => set(n, campo, e.target.value)} style={{ width: w, padding: '2px 4px', fontSize: '0.78rem', textAlign: 'right' }} />
  )

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 'min(1180px, 100%)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '0.8rem 1rem', background: 'linear-gradient(135deg, #1a6ea8, #14547f)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800 }}>Eventos da Reforma Tributária (IBS/CBS)</div>
            {nota && <div style={{ fontSize: '0.75rem', color: '#dbeafe' }}>NF-e nº {nota.numero} • {nota.emitente} • emissão {nota.emissao.split('-').reverse().join('/')}{nota.finNFe === '5' ? ' • NOTA DE CRÉDITO' : ''}</div>}
          </div>
          <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, background: ambiente === 'prod' ? '#fee2e2' : '#fef3c7', color: ambiente === 'prod' ? '#991b1b' : '#92400e' }}>
            {ambiente === 'prod' ? 'PRODUÇÃO' : 'HOMOLOGAÇÃO'}
          </span>
        </div>

        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          {carregando && <div style={{ color: '#64748b' }}>Carregando...</div>}
          {erro && <div style={{ padding: '0.6rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontWeight: 600, fontSize: '0.85rem' }}>{erro}</div>}

          {nota && (
            <>
              <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1b3f7b' }}>Evento</label>
                <select value={tipo} onChange={e => { setTipo(e.target.value as Tipo); setMsg(null) }} style={{ minWidth: 360 }}>
                  {tipos.map(t => <option key={t.codigo} value={t.codigo}>{t.codigo} — {t.nome}</option>)}
                </select>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#475569', background: '#f1f5f9', borderRadius: 8, padding: '0.5rem 0.7rem' }}>{AJUDA[tipo]}</div>

              {tipo === '211128' ? (
                nota.finNFe !== '5'
                  ? <div style={{ fontSize: '0.85rem', color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '0.6rem 0.8rem' }}>Essa nota não é uma Nota de Crédito (finalidade 5), então não cabe aceite de débito.</div>
                  : (
                    <div style={{ display: 'flex', gap: '1.2rem', fontSize: '0.85rem' }}>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="radio" checked={aceite === '1'} onChange={() => setAceite('1')} style={{ width: 'auto' }} /> Aceito o débito</label>
                      <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="radio" checked={aceite === '0'} onChange={() => setAceite('0')} style={{ width: 'auto' }} /> Não aceito</label>
                    </div>
                  )
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', fontSize: '0.78rem', color: '#64748b' }}>
                    Marque os itens do evento e preencha os valores. As colunas "da nota" são só referência (a SEFAZ não aceita valor maior que o da nota).
                    <button className="btn btn-sm" disabled={!marcados.length} onClick={copiarDaNota} style={{ whiteSpace: 'nowrap' }}>Copiar IBS/CBS da nota</button>
                  </div>
                  <div style={{ overflow: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={th}><input type="checkbox" style={{ width: 'auto' }} checked={!!nota.itens.length && marcados.length === nota.itens.length}
                            onChange={e => setLinhas(l => Object.fromEntries(Object.entries(l).map(([k, v]) => [k, { ...v, marcado: e.target.checked }])))} /></th>
                          <th style={th}>Item</th><th style={th}>Produto</th><th style={{ ...th, textAlign: 'right' }}>Qtd</th><th style={th}>Un</th>
                          <th style={{ ...th, textAlign: 'right' }}>Base IBS/CBS</th><th style={{ ...th, textAlign: 'right' }}>IBS da nota</th><th style={{ ...th, textAlign: 'right' }}>CBS da nota</th>
                          {(tipo === '211124' || tipo === '211130') && <>
                            <th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>IBS</th><th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>CBS</th>
                            <th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>{tipo === '211130' ? 'Qtd imobilizada' : 'Qtd perdida'}</th><th style={{ ...th, background: '#0f2a52' }}>Un</th>
                          </>}
                          {tipo === '211150' && <>
                            <th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>Crédito IBS</th><th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>Crédito CBS</th>
                          </>}
                          {tipo === '211110' && <>
                            <th style={{ ...th, background: '#0f2a52' }}>cCredPres</th>
                            <th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>% IBS</th><th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>Crédito IBS</th>
                            <th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>% CBS</th><th style={{ ...th, background: '#0f2a52', textAlign: 'right' }}>Crédito CBS</th>
                          </>}
                        </tr>
                      </thead>
                      <tbody>
                        {nota.itens.map(i => (
                          <tr key={i.nItem} style={{ background: linhas[i.nItem]?.marcado ? '#eff6ff' : '#fff' }}>
                            <td style={td}><input type="checkbox" style={{ width: 'auto' }} checked={!!linhas[i.nItem]?.marcado} onChange={e => set(i.nItem, 'marcado', e.target.checked)} /></td>
                            <td style={td}>{i.nItem}</td>
                            <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${i.cProd} — ${i.xProd}`}>{i.xProd}</td>
                            <td style={tdN}>{fmt(i.qCom, 4)}</td><td style={td}>{i.uCom}</td>
                            <td style={tdN}>{fmt(i.vBC)}</td><td style={tdN}>{fmt(i.vIBS)}</td><td style={tdN}>{fmt(i.vCBS)}</td>
                            {(tipo === '211124' || tipo === '211130') && <>
                              <td style={tdN}>{inp(i.nItem, 'vIBS')}</td><td style={tdN}>{inp(i.nItem, 'vCBS')}</td>
                              <td style={tdN}>{inp(i.nItem, 'quantidade', 80)}</td>
                              <td style={td}><input value={linhas[i.nItem]?.unidade ?? ''} disabled={!linhas[i.nItem]?.marcado} maxLength={6}
                                onChange={e => set(i.nItem, 'unidade', e.target.value.toUpperCase())} style={{ width: 52, padding: '2px 4px', fontSize: '0.78rem' }} /></td>
                            </>}
                            {tipo === '211150' && <><td style={tdN}>{inp(i.nItem, 'vCredIBS')}</td><td style={tdN}>{inp(i.nItem, 'vCredCBS')}</td></>}
                            {tipo === '211110' && <>
                              <td style={td}><input value={linhas[i.nItem]?.cCredPres ?? ''} disabled={!linhas[i.nItem]?.marcado} maxLength={2} placeholder="00"
                                onChange={e => set(i.nItem, 'cCredPres', e.target.value.replace(/\D/g, ''))} style={{ width: 40, padding: '2px 4px', fontSize: '0.78rem' }} /></td>
                              <td style={tdN}>{inp(i.nItem, 'pIBS', 56)}</td><td style={tdN}>{inp(i.nItem, 'vCredIBS')}</td>
                              <td style={tdN}>{inp(i.nItem, 'pCBS', 56)}</td><td style={tdN}>{inp(i.nItem, 'vCredCBS')}</td>
                            </>}
                          </tr>
                        ))}
                        {nota.itens.length === 0 && <tr><td colSpan={16} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>Nenhum item encontrado no XML.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {tipo === '211110' && <div style={{ fontSize: '0.74rem', color: '#92400e' }}>cCredPres: código da tabela oficial de Crédito Presumido (Portal da NF-e). Preencha o grupo do IBS e/ou o da CBS, conforme o código permitir.</div>}
                </>
              )}

              {msg && <div style={{ padding: '0.55rem 0.9rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, background: msg.ok ? '#dcfce7' : '#fee2e2', color: msg.ok ? '#166534' : '#991b1b' }}>{msg.texto}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-primary" onClick={enviar} disabled={enviando || (tipo === '211128' && nota.finNFe !== '5')}>{enviando ? 'Enviando...' : 'Enviar evento'}</button>
              </div>

              <div>
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#1b3f7b', marginBottom: 6 }}>Eventos já enviados para esta nota</div>
                <div style={{ overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr>{['Data e hora', 'Evento', 'Seq.', 'Protocolo', 'Situação', 'Usuário', ''].map(c => <th key={c} style={{ ...th, background: '#475569' }}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {eventos.map(ev => (
                        <tr key={ev.seq}>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{dataHora(ev.dataHora)}</td>
                          <td style={td}>{ev.tpEvento} — {ev.evento}</td>
                          <td style={td}>{ev.nSeqEvento ?? ''}</td>
                          <td style={td}>{ev.protocolo}</td>
                          <td style={td}>
                            <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700, background: ev.cancelado ? '#f1f5f9' : '#dcfce7', color: ev.cancelado ? '#64748b' : '#15803d' }}>{ev.situacao}</span>
                          </td>
                          <td style={td}>{ev.usuario}</td>
                          <td style={td}>
                            {!ev.cancelado && ev.tpEvento !== '110001' && (
                              <button className="btn btn-sm" disabled={enviando} onClick={() => cancelar(ev)} style={{ color: '#b91c1c' }}>Cancelar</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {eventos.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1rem' }}>Nenhum evento da Reforma Tributária enviado para esta nota.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={onClose} style={{ color: '#6b7280' }}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  )
}
